import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  Box,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useDisclosure
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

const GeolocationCheck = ({ onGeolocationGranted, onGeolocationDenied }) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [geolocationStatus, setGeolocationStatus] = useState('checking'); // checking, granted, denied, unsupported
  const [errorMessage, setErrorMessage] = useState('');

  const sendInitialLocation = useCallback(async () => {
    console.log('[GEOLOCATION_CHECK] Enviando ubicación inicial...');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        console.log('[GEOLOCATION] Ubicación obtenida:', position.coords);
        
        // Enviar ubicación al backend
        try {
          const token = localStorage.getItem('auth-token');
          if (!token) {
            console.warn('[GEOLOCATION] No hay token, solo verificando permiso del navegador');
            setGeolocationStatus('granted');
            if (onGeolocationGranted) onGeolocationGranted();
            return;
          }

          const baseURL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) || 'http://localhost:8000';
          const response = await fetch(`${baseURL}/api/ubicaciones/actualizar-posicion`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              timestamp: new Date().toISOString()
            })
          });

          if (response.ok) {
            console.log('[GEOLOCATION] Ubicación enviada correctamente al backend');
            localStorage.setItem('lastPosition', JSON.stringify({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            }));
            localStorage.setItem('lastUpdateTime', Date.now().toString());
          } else {
            console.warn('[GEOLOCATION] Error al enviar ubicación:', response.status);
          }
        } catch (error) {
          console.error('[GEOLOCATION] Error al enviar ubicación:', error);
        }

        setGeolocationStatus('granted');
        if (onGeolocationGranted) onGeolocationGranted();
      },
      (error) => {
        console.error('[GEOLOCATION_CHECK] Error al obtener ubicación:', error.message, 'code:', error.code);
        setGeolocationStatus('denied');
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setErrorMessage(t('geolocation_permission_denied'));
            break;
          case error.POSITION_UNAVAILABLE:
            setErrorMessage(t('geolocation_unavailable'));
            break;
          case error.TIMEOUT:
            setErrorMessage(t('geolocation_timeout'));
            break;
          default:
            setErrorMessage(t('geolocation_error'));
        }
        
        console.log('[GEOLOCATION_CHECK] Abriendo modal de error');
        onOpen();
        if (onGeolocationDenied) onGeolocationDenied();
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  }, [onGeolocationDenied, onGeolocationGranted, onOpen, t]);

  const checkGeolocation = useCallback(async () => {
    console.log('[GEOLOCATION_CHECK] Iniciando verificación...');
    
    // Verificar si el navegador soporta geolocalización
    if (!('geolocation' in navigator)) {
      console.log('[GEOLOCATION_CHECK] Navegador no soporta geolocalización');
      setGeolocationStatus('unsupported');
      setErrorMessage(t('geolocation_unsupported'));
      onOpen();
      if (onGeolocationDenied) onGeolocationDenied();
      return;
    }

    // Verificar que existe custom_device_id (necesario para el tracking)
    const customDeviceId = localStorage.getItem('custom_device_id');
    if (!customDeviceId) {
      console.warn('[GEOLOCATION] No se encontró custom_device_id, generando uno nuevo');
      const newDeviceId = 'device-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now();
      localStorage.setItem('custom_device_id', newDeviceId);
    }

    // Verificar el estado del permiso si está disponible
    if ('permissions' in navigator && navigator.permissions.query) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        console.log('[GEOLOCATION_CHECK] Estado del permiso:', result.state);
        console.log('[GEOLOCATION_CHECK] User Agent:', navigator.userAgent);
        
        if (result.state === 'granted') {
          // Permiso concedido, pero necesitamos enviar ubicación al backend
          await sendInitialLocation();
          return;
        } else if (result.state === 'denied') {
          console.log('[GEOLOCATION_CHECK] Permiso denegado por Permissions API, mostrando modal');
          setGeolocationStatus('denied');
          setErrorMessage(t('geolocation_denied'));
          onOpen();
          if (onGeolocationDenied) onGeolocationDenied();
          return;
        }
        // Si result.state === 'prompt', continuamos para solicitar permiso
        console.log('[GEOLOCATION_CHECK] Permiso en estado "prompt", solicitando ubicación...');
      } catch (err) {
        console.warn('[GEOLOCATION_CHECK] Permissions API falló o no soportada:', err);
        // En Opera y algunos navegadores, la Permissions API puede no funcionar bien
        // Continuamos con getCurrentPosition que es más compatible
      }
    } else {
      console.log('[GEOLOCATION_CHECK] Permissions API no disponible, usando getCurrentPosition directamente');
    }

    // Solicitar geolocalización directamente (funciona en más navegadores)
    console.log('[GEOLOCATION_CHECK] Solicitando geolocalización...');
    sendInitialLocation();
  }, [onGeolocationDenied, onOpen, sendInitialLocation, t]);

  useEffect(() => {
    checkGeolocation();
  }, [checkGeolocation]);

  const handleRetry = () => {
    onClose();
    setGeolocationStatus('checking');
    setErrorMessage('');
    checkGeolocation();
  };

  return (
    <Modal isOpen={isOpen} onClose={() => {}} closeOnOverlayClick={false} isCentered size="lg">
      <ModalOverlay className="geolocation-modal-overlay" bg="blackAlpha.700" backdropFilter="blur(10px)" />
      <ModalContent className="geolocation-modal-content">
        <ModalHeader className="geolocation-modal-header" textAlign="center" fontSize="xl" color="red.600" pb={2}>
          📍 {t('geolocation_required_title')}
        </ModalHeader>
        <ModalBody className="geolocation-modal-body" pb={4}>
          <VStack spacing={3} align="stretch">
            <Alert
              className="geolocation-alert"
              status={geolocationStatus === 'unsupported' ? 'error' : 'warning'}
              variant="left-accent"
              borderRadius="md"
            >
              <AlertIcon />
              <Box>
                <AlertTitle className="geolocation-alert-title" fontSize="md" color="black">
                  {geolocationStatus === 'unsupported' 
                    ? t('geolocation_unsupported_title')
                    : t('geolocation_denied_title')}
                </AlertTitle>
                <AlertDescription className="geolocation-alert-description" fontSize="sm" color="black">
                  {errorMessage || t('geolocation_required_message')}
                </AlertDescription>
              </Box>
            </Alert>

            {/* Instrucciones compactas */}
            <Box className="geolocation-instructions" fontSize="sm" color="gray.600">
              <Text className="geolocation-instructions-title" fontWeight="semibold" mb={1}>{t('geolocation_instructions_title')}</Text>
              <Text>1️⃣ {t('geolocation_step_1')}</Text>
              <Text>2️⃣ {t('geolocation_step_2')}</Text>
              <Text>3️⃣ {t('geolocation_step_3')}</Text>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter className="geolocation-modal-footer" justifyContent="center" pt={2}>
          <Button className="geolocation-retry-button" colorScheme="blue" onClick={handleRetry} size="md">
            🔄 {t('geolocation_retry')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default GeolocationCheck;
